import React, { useEffect, useState } from 'react';
import { MenuItem, MenuItemProps } from '@mui/material';
import axios from '@/_utils/axios';
import Autocomplete from './Form/Autocomplete';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { Customer } from '@/redux/slices/slice_types';
import { selectBillableCustomers } from '@/redux/selectors/dataTableFiltersSelectors';
import { getBillableCustomers } from '@/redux/slices/constantsSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCanAddCustomer } from '@/redux/selectors/authSelectors';

interface BillableCustomerSearchProps {
  onSelect: (e: any, selectedOption: any, ...rest: any) => void;
  value: any;
  label?: string;
  skipDispatch?: boolean;
}

const filter = createFilterOptions();
const BillableCustomerSearch = ({ onSelect, value, label = 'Billable Customer', skipDispatch = false }: BillableCustomerSearchProps) => {
  const canAddCustomer = useAppSelector(selectCanAddCustomer);
  const dispatch = useAppDispatch();
  const [searchInput, setSearchInput] = useState('');
  const [filteredBillableCustomers, setFilteredBillableCustomers] = useState<Customer[]>([]);
  const [allBillableCustomers, setAllBillableCustomers] = useState<Customer[]>([]);
  const billableCustomers = useAppSelector(selectBillableCustomers);

  useEffect(() => {
    if (!skipDispatch) {
      dispatch(getBillableCustomers());
    }
  }, [skipDispatch, dispatch]);

  useEffect(() => {
    setAllBillableCustomers(billableCustomers);
    setFilteredBillableCustomers(billableCustomers);
  }, [billableCustomers]);

  useEffect(() => {
    if (searchInput.length) {
      const filteredCustomers = allBillableCustomers.filter((a) => a);
      setFilteredBillableCustomers(filteredCustomers);
    } else {
      setFilteredBillableCustomers(allBillableCustomers);
    }
    return () => setFilteredBillableCustomers([]);
  }, [searchInput]);

  const renderOption = (props: MenuItemProps, option: Customer | string) => {
    if (!option) return '';
    
    // Handle string values (from draft resume before conversion)
    if (typeof option === 'string') {
      return (
        <MenuItem key={option} role="option" {...props} style={{ display: 'block' }}>
          <div>{option}</div>
        </MenuItem>
      );
    }
    
    // Handle customer objects
    return (
      <MenuItem key={option.id} role="option" {...props} style={{ display: 'block' }}>
        <div>{option.name}</div>
      </MenuItem>
    );
  };

  const onChange = (e: React.SyntheticEvent, selectedOption: any, ...rest: unknown[]) => {
    if (selectedOption.customAction && selectedOption.inputValue) {
      axios
        .post('/core/customers/create/', {
          name: selectedOption.inputValue,
          is_billable_customer: true
        })
        .then((res) => {
          const newOption = res.data.data;
          setAllBillableCustomers(allBillableCustomers.concat(newOption));
          onSelect(e, newOption, ...rest);
        });
    } else {
      onSelect(e, selectedOption, ...rest);
    }
  };

  return (
    <Autocomplete
      options={filteredBillableCustomers}
      label={label}
      value={value || null}
      onInputChange={(e, val) => setSearchInput(val)}
      renderOption={renderOption}
      noOptionsText={label ? `No ${label.toLowerCase()} found.` : 'None found.'}
      openOnFocus={false}
      onChange={onChange}
      placeholder="Start typing to search"
      blurOnSelect
      isOptionEqualToValue={(option, value) => {
        // Handle string values
        if (typeof option === 'string' && typeof value === 'string') {
          return option === value;
        }
        // Handle mixed types (string vs object)
        if (typeof option === 'string' && typeof value === 'object' && value?.name) {
          return option === value.name;
        }
        if (typeof value === 'string' && typeof option === 'object' && option?.name) {
          return value === option.name;
        }
        // Handle object values
        return option?.id === value?.id;
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const { inputValue } = params;
        // Suggest the creation of a new value
        const isExisting = options.some((option) => inputValue === option.name);
        if (inputValue !== '' && !isExisting && canAddCustomer) filtered.push({ inputValue, name: `Add "${inputValue}"`, customAction: true, id: 0 });
        return filtered;
      }}
      getOptionLabel={(option) => {
        // Handle string values (from draft resume before conversion)
        if (typeof option === 'string') return option;
        // Add "xxx" option created dynamically
        if (option.inputValue) return option.inputValue;
        // Regular option
        return option.name || '';
      }}
    />
  );
};

export default BillableCustomerSearch;
